import React from 'react';
import styled from 'styled-components';
import { PageCard } from 'src/common/PageCard';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { SparkleSvg } from 'src/assets/dynamicSvg';
import { SuggestedFrequency } from '../types';
import { TRIANGULATED } from '../util/frequency';
import { useLoadingAction } from '../useLoadingAction';
import { AcceptedPill } from './AcceptedPill';
import { PrimaryButton } from './SuggestionButtons';
import { QuantNote } from './QuantNote';
import { FrequencySourceCard } from './FrequencySourceCard';

const Card = styled(PageCard)`
  background: ${({ theme }) => `${theme.colors.aliceBlue}4d`};
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
`;

const Section = styled.div<{ $divided?: boolean }>`
  padding: 24px 20px;
  ${({ theme, $divided }) => $divided && `border-bottom: 1px solid ${theme.colors.stroke};`}
`;

const ListSection = styled.div`
  padding: 16px 20px 24px;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SuggestionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Headline = styled(Poppins)`
  background: ${({ theme }) => theme.colors.gradient};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const SourceList = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  overflow: hidden;

  & > * + * {
    border-top: 1px solid ${({ theme }) => theme.colors.stroke};
  }
`;

interface FrequencyPanelProps {
  frequency: SuggestedFrequency;
  pending: boolean;
  onChoose: (chosenValue: number, chosenSourceId: string) => Promise<unknown>;
}

export const FrequencyPanel: React.FC<FrequencyPanelProps> = ({ frequency, pending, onChoose }) => {
  const { loadingKey, run } = useLoadingAction();
  const triangulatedAccepted = frequency.chosenSourceId === TRIANGULATED;

  return (
    <Card>
      <Section $divided>
        <Eyebrow>
          <SparkleSvg size={16} />
          <Poppins px={16} weight={600} color="prussianBlue" css="text-transform: uppercase;">
            Suggested Frequency
          </Poppins>
        </Eyebrow>
      </Section>

      <Section $divided>
        <SuggestionRow>
          <Headline px={18} weight={500}>
            {frequency.display}
          </Headline>
          {triangulatedAccepted ? (
            <AcceptedPill />
          ) : (
            <PrimaryButton
              label="Accept suggestion"
              loading={loadingKey === TRIANGULATED}
              disabled={pending}
              onClick={() => run(TRIANGULATED, () => onChoose(frequency.suggestedValue, TRIANGULATED))}
            />
          )}
        </SuggestionRow>
        <Spacer $px={32} />
        <QuantNote note={frequency.note} />
      </Section>

      <ListSection>
        <Poppins px={18} weight={500} color="prussianBlue">
          Industry Frequency Values
        </Poppins>
        <Spacer $px={24} />
        <SourceList>
          {frequency.sources.map((source) => (
            <FrequencySourceCard
              key={source.id}
              source={source}
              accepted={frequency.chosenSourceId === source.id}
              loading={loadingKey === source.id}
              disabled={pending}
              onUse={() => run(source.id, () => onChoose(source.value, source.id))}
            />
          ))}
        </SourceList>
      </ListSection>
    </Card>
  );
};
